import { Fragment, type ReactElement } from 'react';
import { Text, type TextWeight } from '../typography/Text/Text';
import styles from './Pill.module.css';

type VariantProps =
  | {
      variant: 'label' | 'small';
      htmlFor: string;
    }
  | {
      variant?: 'button';
    };

type IconProps =
  | {
      icon: ReactElement;
      iconPosition: 'left' | 'right';
    }
  | {
      icon?: undefined;
    };

type Props = {
  label: string;
  testid?: string;
  active?: boolean;
  className?: string;
  onClick?: () => void;
  textVariant?: TextWeight;
  focusable?: boolean;
} & IconProps &
  VariantProps;

function renderChildren(props: Props) {
  const { label, testid, textVariant, icon } = props;
  const child = (
    <Text
      testid={`${testid}-label`}
      weight={textVariant}
      className={styles.nowrap}
    >
      {label}
    </Text>
  );

  if (icon !== undefined) {
    const { iconPosition } = props;
    if (iconPosition === 'right') {
      return (
        <Fragment key="icon-position-right">
          {child}
          <span
            className={styles['icon-container']}
            data-testid={`${testid}-icon-right`}
          >
            {icon}
          </span>
        </Fragment>
      );
    }

    return (
      <Fragment key="icon-position-left">
        <span
          className={styles['icon-container']}
          data-testid={`${testid}-icon-left`}
        >
          {icon}
        </span>
        {child}
      </Fragment>
    );
  }

  return child;
}

export const Pill = (props: Props) => {
  const { onClick, variant, className: classNameProp, testid, active } = props;
  const className = active === true ? styles.active : styles.pill;
  const buttonClassName = `${className} ${
    classNameProp === undefined ? '' : classNameProp
  } ${props.variant == 'small' ? styles.small : ''}`;

  const child = renderChildren(props);

  if (variant === 'label' || variant === 'small') {
    const { htmlFor } = props;
    return (
      <label
        onClick={onClick}
        htmlFor={htmlFor}
        data-testid={testid}
        className={buttonClassName}
      >
        {child}
      </label>
    );
  }

  return (
    <button onClick={onClick} data-testid={testid} className={buttonClassName}>
      {child}
    </button>
  );
};
